import { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const THEME_KEY = "ams_theme";

function initialTheme() {
    const queryTheme = new URLSearchParams(window.location.search).get("theme");
    if (queryTheme === "light" || queryTheme === "dark") return queryTheme;

    const savedTheme = window.localStorage?.getItem(THEME_KEY);
    if (savedTheme === "light" || savedTheme === "dark") return savedTheme;

    const cookie = document.cookie
        .split("; ")
        .find((entry) => entry.startsWith(`${THEME_KEY}=`))
        ?.split("=")[1];
    return cookie === "light" || cookie === "dark" ? cookie : "dark";
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(initialTheme);
    const isLiteRoute = window.location.pathname === "/affichage";

    useEffect(() => {
        if (isLiteRoute) return;
        document.documentElement.dataset.theme = theme;
        document.documentElement.style.colorScheme = theme;
        window.localStorage?.setItem(THEME_KEY, theme);

        const domain = window.location.hostname.endsWith(".amservices.eu")
            ? "; Domain=.amservices.eu"
            : "";
        document.cookie = `${THEME_KEY}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax${domain}`;
    }, [theme, isLiteRoute]);

    useEffect(() => {
        if (isLiteRoute) return undefined;

        const handleMessage = (event) => {
            if (event.data?.type === "ams-theme" && ["light", "dark"].includes(event.data.theme)) {
                setTheme(event.data.theme);
            }
        };
        const handleStorage = (event) => {
            if (event.key === THEME_KEY && ["light", "dark"].includes(event.newValue)) {
                setTheme(event.newValue);
            }
        };

        window.addEventListener("message", handleMessage);
        window.addEventListener("storage", handleStorage);
        return () => {
            window.removeEventListener("message", handleMessage);
            window.removeEventListener("storage", handleStorage);
        };
    }, [isLiteRoute]);

    const value = useMemo(() => ({
        theme,
        toggleTheme: () => setTheme((current) => current === "dark" ? "light" : "dark")
    }), [theme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const value = useContext(ThemeContext);
    if (!value) throw new Error("ThemeProvider manquant");
    return value;
}
